// #806 — wildcard taxonomy-gap detector view.
//
// Population-level aggregate over `wildcard_segments`. Surfaces
// recurring wildcard topics (≥ min_users users) with high avg
// engagement (≥ min_avg_engagement) — candidates for new card
// types in the catalogue. Bas's primary use: "what should I add
// to the catalogue next?"
//
// Privacy: this view shows ONLY topic + counts + avg engagement.
// Per-segment text_excerpts (admin-only on per-conv view) are
// NOT exposed in population aggregates — even admins shouldn't
// inadvertently cross the cross-user transcript-content line.
// Backend's `get_wildcard_taxonomy_gaps` SELECT enforces this.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminFetch } from './userSubPageStyles';

const VERSION = 'v1.0.0';

function engagementBadge(avg) {
  if (avg == null) return <span style={{ color: '#ccc' }}>—</span>;
  let bg, fg;
  if (avg >= 70)      { bg = '#E8F5E9'; fg = '#2E7D32'; }
  else if (avg >= 50) { bg = '#FFF3E0'; fg = '#E65100'; }
  else                { bg = '#FFEBEE'; fg = '#C62828'; }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: '0.78rem', fontWeight: 700,
      background: bg, color: fg,
    }}>{avg}</span>
  );
}

export default function AdminCardsWildcardGaps() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [minUsers, setMinUsers] = useState(5);
  const [minAvgEngagement, setMinAvgEngagement] = useState(60);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    (async () => {
      try {
        const qs = `?days=${days}&min_users=${minUsers}&min_avg_engagement=${minAvgEngagement}`;
        const r = await adminFetch(`/api/admin/cards/wildcard-gaps${qs}`);
        if (cancelled) return;
        setData(r || { rows: [] });
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [days, minUsers, minAvgEngagement]);

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Link to="/admin/cards/effectiveness" style={{ fontSize: '0.85rem' }}>
          ← back to Cards effectiveness
        </Link>
        <h2 style={{ marginTop: 8 }}>
          🌱 Wildcard taxonomy gaps <span style={{ color: '#aaa', fontWeight: 400, fontSize: '0.78rem' }}>{VERSION}</span>
        </h2>
        <p style={{ color: '#666', fontSize: '0.88rem' }}>
          Recurring wildcard topics (no matching card type) that the
          user engaged warmly with. Each row is a catalogue gap —
          if a topic appears across several users with high
          engagement, the catalogue is missing a card type the
          population would actually use.
        </p>

        {/* Filter controls */}
        <div style={{
          display: 'flex', gap: 14, alignItems: 'center',
          margin: '14px 0 18px', fontSize: '0.88rem',
        }}>
          <label>
            Lookback (days):{' '}
            <input type="number" value={days} min={1} max={365}
              onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value || '30', 10))))}
              style={{ width: 60 }} />
          </label>
          <label>
            Min users:{' '}
            <input type="number" value={minUsers} min={1} max={500}
              onChange={(e) => setMinUsers(Math.max(1, parseInt(e.target.value || '5', 10)))}
              style={{ width: 50 }} />
          </label>
          <label>
            Min avg engagement:{' '}
            <input type="number" value={minAvgEngagement} min={0} max={100}
              onChange={(e) => setMinAvgEngagement(Math.max(0, Math.min(100, parseInt(e.target.value || '60', 10))))}
              style={{ width: 50 }} />
          </label>
        </div>

        {error && <p className="error-state">Error: {error}</p>}
        {data && data.rows && data.rows.length === 0 && (
          <p className="empty-state">
            No wildcard topics meet the current thresholds. This
            either means the catalogue covers the population well,
            or the post-call segmenter hasn't accumulated enough
            data yet. Try lowering the thresholds or widening the
            lookback window.
          </p>
        )}
        {data && data.rows && data.rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem',
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Topic</th>
                  <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Users</th>
                  <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Segments</th>
                  <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Avg engagement</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.topic} style={{ borderBottom: '1px solid #f3f3f3' }}>
                    <td style={{ padding: '8px 6px', fontWeight: 500 }}>{r.topic}</td>
                    <td style={{ padding: '8px 6px', color: '#666' }}>{r.user_count}</td>
                    <td style={{ padding: '8px 6px', color: '#666' }}>{r.segment_count}</td>
                    <td style={{ padding: '8px 6px' }}>{engagementBadge(r.avg_engagement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={{
          color: '#aaa', fontSize: '0.78rem', fontStyle: 'italic',
          marginTop: 18,
        }}>
          Privacy note: this view aggregates only topic labels +
          counts + averages. Per-segment verbatim excerpts (visible
          on the per-conversation view) are deliberately NOT
          surfaced in population aggregates.
        </p>
      </div>
    </AdminLayout>
  );
}
